import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import { toast } from 'react-toastify';
import moment from 'moment';
import InputField from '../../../_common/Form/TextFields/InputField';
import Select from '../../../_common/Form/Selects/Select/Select';
import Button from '../../../_common/Form/Buttons/Button';
import classes from '../team.module.scss';
import { sentAddUserRequest, sentEditUserRequest } from '../../../../store/actions/team';
import utils from '../../../../utils/utils';
import { getOneUserRequest } from '../../../../store/actions/users';
import accountHelpers from '../../../../utils/accountHelpers';
import Loader from '../../../_common/Loader/Loader';
import TeamChangePasswordModal from '../TeamChangePasswordModal/TeamChangePasswordModal';
import Checkbox from '../../../_common/Form/Checkbox/Checkbox';
import DOBPicker from '../../../_common/Form/DatePickers/DOBPicker/DOBPicker';
import { getOnePatient, updatePatientRequest } from '../../../../store/actions/patients';

const selectRole = ['Pharmacy', 'Sales Rep', 'Physician', 'Health', 'Patient'];

const baseFields = [
  {
    name: 'Email',
    path: 'email',
  },
  {
    name: 'Username',
    path: 'username',
  },
  {
    name: 'Name',
    path: 'name',
  },
];

const fieldsForPatient = [
  {
    name: 'Insurance ID',
    path: 'medicalId',
  },
  {
    name: 'First Name',
    path: 'name',
  },
  {
    name: 'Last Name',
    path: 'lastName',
  },
  {
    name: 'Phone',
    path: 'phone',
  },
  {
    name: 'Date of birth',
    path: 'dob',
  },
  {
    name: 'Zip',
    path: 'zip',
  },
];

function TeamAddOrEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useParams();

  const { menu } = useSelector((store) => store.menu);

  const backUrl = menu.find(({ name }) => name === 'My Team')?.path;

  const [requiredError, setRequiredError] = useState({
    email: '',
    username: '',
    role: '',
    name: '',
    lastName: '',
    phone: '',
    medicalId: '',
    dob: '',
    zip: '',
  });

  const [errorMassages, setErrorMessages] = useState('');

  const [requestData, setRequestData] = useState({
    email: '',
    username: '',
    role: '',
    name: '',
    lastName: '',
    phone: '',
    medicalId: '',
    dob: '',
    active: false,
    zip: '',
  });

  const [userInfo, setUserInfo] = useState({
    isAccountLocked: false,
    lockedTime: 0,
  });

  const { isAccountLocked, lockedTime } = userInfo;

  const [loading, setLoading] = useState(true);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const infoFields = useMemo(() => {
    if (requestData.role === 'Patient') {
      return [...baseFields.filter(({ name }) => name !== 'Name'), ...fieldsForPatient];
    }

    return baseFields;
  }, [requestData.role]);

  useEffect(() => {
    if (userId) {
      (async () => {
        setLoading(true);

        const { payload } = await dispatch(getOneUserRequest({ userId, searcherId: accountHelpers.getUser().id }));

        const {
          email, name, username, role, id, active, lockedDateTimeUTC, patientId,
        } = payload.data.data.response[0];

        if (role === 'Patient') {
          const response = await dispatch(getOnePatient({ patientId, userId: accountHelpers.getUser().id }));
          const {
            dob, addressZipCode, lastName, phone, medicalId,
          } = response.payload.data.data.response[0];
          await setUserInfo({
            dob, addressZipCode, lastName, phone, medicalId,
          });

          await setRequestData({
            dob, lastName, addressZipCode, phone, medicalId,
          });
        }

        const ACCOUNT_LOCKOUT_MINUTES = 30;
        const nowUtcString = moment.utc().format('YYYY-MM-DD HH:mm:ss');
        const accountLockedTill = lockedDateTimeUTC
          ? moment(lockedDateTimeUTC).add('m', ACCOUNT_LOCKOUT_MINUTES).format('YYYY-MM-DD HH:mm:ss')
          : null;
        const isLocked = accountLockedTill ? moment(accountLockedTill).isAfter(nowUtcString) : false;

        setUserInfo((prev) => ({
          ...prev,
          email,
          name,
          username,
          role,
          id,
          active,
          isAccountLocked: isLocked,
          lockedTime: ACCOUNT_LOCKOUT_MINUTES || 0,
        }));

        setLoading(false);

        setRequestData((prev) => ({
          ...prev, email, name, username, role, id, active,
        }));
      })();
    }
  }, []);

  const submitAddOrEditUsers = async (e) => {
    e.preventDefault();

    let hasError = false;
    let requiredFields = ['email', 'username', 'role'];

    if (requestData.role === 'Patient') {
      if (userId) {
        requiredFields = [...requiredFields, 'name', 'lastName', 'dob', 'addressZipCode', 'phone', 'medicalId'];
      } else {
        requiredFields = [...requiredFields, 'name', 'lastName', 'dob', 'zip', 'phone', 'medicalId'];
      }
    }

    _.forEach(requestData, (val, key) => {
      if ((!val) && requiredFields.includes(key)) {
        setRequiredError((prev) => ({ ...prev, [key]: 'Field is required' }));

        hasError = true;
      }
    });

    if (requestData.email && !utils.isEmail(requestData.email)) {
      setRequiredError((prev) => ({ ...prev, email: 'Invalid email' }));

      hasError = true;
    }

    if (requestData.dob && requestData.dob.length !== 10) {
      setRequiredError((prev) => ({ ...prev, dob: 'Invalid date' }));

      hasError = true;
    }

    if (hasError) {
      toast.error('Please fill this fields');
    } else {
      if (requestData.role !== 'Patient') {
        delete requestData?.lastName;
        delete requestData?.dob;
        delete requestData?.zip;
        delete requestData?.phone;
        delete requestData?.medicalId;
      }

      const newQuery = utils.deleteEmptyKeys(requestData);

      try {
        let data;

        if (userId) {
          data = await dispatch(sentEditUserRequest(newQuery));

          if (newQuery.role === 'Patient') {
            const { status } = await dispatch(updatePatientRequest({
              lastName: newQuery.lastName,
              dob: newQuery.dob,
              addressZipCode: newQuery.addressZipCode,
              id: newQuery.id,
              userId: accountHelpers.getUser().id,
              firstName: newQuery.name,
              phone: newQuery.phone,
              medicalId: newQuery.medicalId,
            }));

            if (data.status === 'ok' && status === 'ok') {
              navigate(backUrl);

              toast.success('User successfully updated');
            } else {
              setErrorMessages(data.payload.data.message);

              toast.error(`${data.payload.data.message}` || 'something for filed');
            }
            return;
          }
        } else {
          data = await dispatch(sentAddUserRequest(newQuery));
        }

        if (data.status === 'ok') {
          navigate(backUrl);

          toast.success('User successfully created');
        } else {
          setErrorMessages(data.payload.data.message);

          toast.error(`${data.payload.data.message}` || 'something for filed');
        }
      } catch (e) {
        console.warn(e);
      }
    }
  };

  const unlockUser = async () => {
    const sendData = utils.deleteEmptyKeys(userInfo);

    delete sendData.isAccountLocked;
    delete sendData.lockedTime;

    sendData.lockedCount = 0;
    sendData.lockedDateTimeUTC = null;

    const data = await dispatch(sentEditUserRequest(sendData));

    if (data.status === 'ok') {
      navigate(backUrl);
    } else {
      setErrorMessages(data.payload.data.message);
    }
  };

  const newUserChange = (value, path) => {
    if (path === 'zip') {
      if (!Number.isNaN(+value) || value === '') {
        setRequestData((prev) => ({ ...prev, [path]: value }));
      }
    } else {
      setRequestData((prev) => ({ ...prev, [path]: value }));
    }

    setRequiredError((prev) => ({ ...prev, [path]: '' }));
  };

  const changeSelectRole = (option) => {
    setRequiredError({
      email: '',
      username: '',
      role: '',
      name: '',
      lastName: '',
      dob: '',
      zip: '',
      phone: '',
      medicalId: '',
    });

    if (option === 'Sales Rep') {
      setRequestData((prev) => ({ ...prev, role: 'Rep' }));
    } else {
      setRequestData((prev) => ({ ...prev, role: option }));
    }

    setRequiredError((prev) => ({ ...prev, role: '' }));
  };

  const changePasswordClick = () => {
    setIsOpenModal(true);
  };

  const onKeyPress = (e, validationType, isPaste) => {
    if (validationType === 'phone') {
      utils.keyPressPhoneNumber(e, isPaste);
    }
  };

  return (
    <div className={classes.team_add_wrapper}>
      <h2>{userId ? 'Edit Member' : 'Add team member'}</h2>
      {userId && loading
        ? (
          <div className={classes.loader_wrapper}>
            <Loader />
          </div>
        )

        : (
          <form onSubmit={submitAddOrEditUsers}>
            <div className={classes.add_input_wrapper}>
              {userId && accountHelpers.getUser().role !== 'Admin'
                ? (
                  <InputField
                    label="Role"
                    value={requestData.role}
                    disabled
                  />
                )
                : (
                  <Select
                    label="Role"
                    options={accountHelpers.getUser().role === 'Rep' ? ['Physician'] : selectRole}
                    currentOption={requestData.role}
                    error={requiredError.role}
                    onChange={(option) => {
                      changeSelectRole(option);
                    }}
                  />
                )}
            </div>

            {infoFields.map((i) => (
              <div className={classes.add_input_wrapper} key={i.name}>
                {i.path === 'dob'
                  ? (
                    <DOBPicker
                      label="Date of Birth"
                      date={requestData[i.path]}
                      onChange={(date) => newUserChange(date, i.path)}
                      autoValidation={false}
                      error={requiredError[i.path]}
                    />
                  )
                  : (
                    <InputField
                      label={i.name}
                      value={requestData[i.path === 'zip' && requestData.role === 'Patient' && userId
                        ? 'addressZipCode'
                        : i.path]}
                      onChange={(value) => {
                        newUserChange(value, i.path === 'zip' && requestData.role === 'Patient' && userId
                          ? 'addressZipCode'
                          : i.path);
                      }}
                      onKeyPress={(e) => onKeyPress(e, i.path)}
                      onPaste={(e) => onKeyPress(e, i.path, true)}
                      error={requiredError[i.path === 'zip' && requestData.role === 'Patient' && userId
                        ? 'addressZipCode'
                        : i.path]}
                    />
                  )}
              </div>
            ))}

            <div className={classes.checkbox_wrapper}>
              <Checkbox
                label="Active"
                onChange={(checked) => newUserChange(checked, 'active')}
                checked={requestData.active}
              />
            </div>

            {isAccountLocked && (
              <div className={classes.locked_wrapper}>
                This account has been locked
                {' '}
                <b>
                  for
                  {' '}
                  {lockedTime}
                  {' '}
                  minutes
                </b>
                {' '}
                due to too many wrong password attempts!

                <Button
                  className={classes.unlock_btn}
                  title="Unlock account"
                  buttonColor="red"
                  reverseColor
                  onClick={unlockUser}
                />
              </div>
            )}

            {userId && (
              <div className={classes.changePassword}>
                <h3>Password</h3>

                <p onClick={changePasswordClick}>Change Password</p>

                <TeamChangePasswordModal
                  onClose={() => setIsOpenModal(false)}
                  userId={userId}
                  isOpen={isOpenModal}
                  close={() => setIsOpenModal(false)}
                />
              </div>
            )}

            <div className={classes.add_button_wrapper}>
              {errorMassages && <div className={classes.error}>{errorMassages}</div>}

              <div>
                <Button
                  title="Save"
                  type="submit"
                  reverseColor
                />
              </div>

              <div>
                <Button
                  title="Cancel"
                  href="/team"
                  clearButton
                />
              </div>
            </div>
          </form>
        )}
    </div>
  );
}

export default TeamAddOrEdit;
